/**
 * UnifiedBottomPanel — Shared floating bottom panel shell with tab system.
 *
 * Used by CharacterDesigner, LocationDesigner, ObjectDesigner, ImageGenerator,
 * and StoryboardDesigner.
 * This component provides only the panel container, tab bar, and open/close animation.
 * Tab content is rendered via a children render function (slot pattern).
 *
 * Follows floating panel pattern: nopan nodrag nowheel, inverse-zoom scaling,
 * spring animation (stiffness 400, damping 25).
 */

import { memo, type ReactNode } from 'react'
import { useViewport } from 'reactflow'
import { motion, AnimatePresence } from 'framer-motion'
import { SlidersHorizontal, Wand2, LayoutGrid, Mic, type LucideIcon } from 'lucide-react'
import { cn } from '../../../../lib/utils'
import { useReadOnly } from '../../../../contexts/ReadOnlyContext'

// ---------------------------------------------------------------------------
// Tab configuration types & constants
// ---------------------------------------------------------------------------

export interface TabDefinition {
  id: string
  label: string
  icon: LucideIcon
  disabled?: boolean
  disabledTooltip?: string
}

/** CharacterDesigner: Config, Refine, Variants, Voice */
export const CHARACTER_TABS: TabDefinition[] = [
  { id: 'config', label: 'Config', icon: SlidersHorizontal },
  { id: 'refine', label: 'Refine', icon: Wand2 },
  { id: 'variants', label: 'Variants', icon: LayoutGrid },
  { id: 'voice', label: 'Voice', icon: Mic },
]

/** LocationDesigner: Config, Refine, Variants */
export const LOCATION_TABS: TabDefinition[] = [
  { id: 'config', label: 'Config', icon: SlidersHorizontal },
  { id: 'refine', label: 'Refine', icon: Wand2 },
  { id: 'variants', label: 'Variants', icon: LayoutGrid },
]

/** ObjectDesigner: Config, Refine, Variants */
export const OBJECT_TABS: TabDefinition[] = [
  { id: 'config', label: 'Config', icon: SlidersHorizontal },
  { id: 'refine', label: 'Refine', icon: Wand2 },
  { id: 'variants', label: 'Variants', icon: LayoutGrid },
]

/** ImageGenerator: Config, Refine, Variants */
export const IMAGE_GEN_TABS: TabDefinition[] = [
  { id: 'config', label: 'Config', icon: SlidersHorizontal },
  { id: 'refine', label: 'Refine', icon: Wand2 },
  { id: 'variants', label: 'Variants', icon: LayoutGrid },
]

/** StoryboardDesigner: Config, Refine, Variants */
export const STORYBOARD_TABS: TabDefinition[] = [
  { id: 'config', label: 'Config', icon: SlidersHorizontal },
  { id: 'refine', label: 'Refine', icon: Wand2 },
  { id: 'variants', label: 'Variants', icon: LayoutGrid },
]

/** Default tab when panel first opens */
export const DEFAULT_TAB = 'config'

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface UnifiedBottomPanelProps {
  /** Tab definitions for this node type */
  tabs: TabDefinition[]
  /** Currently active tab ID */
  activeTab: string
  /** Tab switch callback */
  onTabChange: (tabId: string) => void
  /** Whether the panel is visible/open */
  isVisible: boolean
  /** Optional max-height override for the content area in px (default: 340px) */
  contentHeight?: number
  /** Optional minimum width (px) for non-variants tabs */
  minWidth?: number
  /** Render function receiving the active tab ID — slot pattern for tab content */
  children: (activeTab: string) => ReactNode
  /** Optional footer rendered below the scroll area but inside the panel.
   *  Flyout popovers in the footer escape the overflow-hidden clipping. */
  footer?: (activeTab: string) => ReactNode
}

const UnifiedBottomPanel = memo(function UnifiedBottomPanel({
  tabs,
  activeTab,
  onTabChange,
  isVisible,
  contentHeight,
  minWidth,
  children,
  footer: _footer,
}: UnifiedBottomPanelProps) {
  const readOnly = useReadOnly()
  const { zoom } = useViewport()
  const scale = activeTab === 'variants' ? 1 : Math.min(2, Math.max(0.5, 1 / zoom))

  const widthExpr = minWidth
    ? `max(calc(100% + 120px), ${minWidth}px)`
    : 'calc(100% + 120px)'

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -10, scale, x: '-50%' }}
          animate={{ opacity: 1, y: 0, scale, x: '-50%' }}
          exit={{ opacity: 0, y: -10, scale, x: '-50%' }}
          transition={{
            type: 'spring',
            stiffness: 400,
            damping: 25,
            opacity: { duration: 0.15 },
            scale: { duration: 0 },
          }}
          className="absolute z-50 nopan nodrag nowheel"
          style={{
            top: '100%',
            left: '50%',
            marginTop: 8,
            transformOrigin: 'top center',
            width: widthExpr,
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          data-testid="unified-bottom-panel"
        >
          <div className="bg-surface-1 text-foreground border border-border rounded-xl shadow-2xl">
            {/* Tab Bar */}
            <div className="flex gap-1 p-2 border-b border-border-subtle">
              {tabs.map((tab) => {
                const isActive = activeTab === tab.id
                const Icon = tab.icon
                return (
                  <button
                    key={tab.id}
                    onClick={tab.disabled ? undefined : () => onTabChange(tab.id)}
                    disabled={tab.disabled}
                    className={cn(
                      'flex-1 flex flex-col items-center gap-0.5 px-3 py-1.5 text-xs font-medium rounded-lg relative transition-all duration-200 group',
                      tab.disabled
                        ? 'opacity-40 cursor-not-allowed text-muted-foreground'
                        : isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground'
                    )}
                    data-testid={`tab-${tab.id}`}
                  >
                    <Icon size={14} data-testid={`tab-icon-${tab.id}`} />
                    {tab.label}
                    {tab.disabled && tab.disabledTooltip && (
                      <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-surface-2 text-foreground text-xxs px-2 py-1 rounded shadow-lg border border-border opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                        {tab.disabledTooltip}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>

            {/* Tab Content — overflow hidden, individual tabs handle scrolling */}
            <div
              className={cn(
                'rounded-b-xl nowheel overflow-hidden flex flex-col',
                readOnly && 'pointer-events-none select-text opacity-70',
              )}
              style={{ maxHeight: contentHeight ? `${contentHeight}px` : '340px' }}
              data-testid="tab-content-area"
            >
              {children(activeTab)}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
})

UnifiedBottomPanel.displayName = 'UnifiedBottomPanel'

export { UnifiedBottomPanel }
